<script setup>
import ActionButton from '#elements/ActionButton.vue'
import FormFieldPassword from '#elements/FormFieldPassword.vue'
import FormFieldText from '#elements/FormFieldText.vue'
import httpStatus from '#helpers/httpStatus.js'
import Logger from '#helpers/logging/Logger.js'
import { useHead } from '@unhead/vue'
import { defineAsyncComponent, onMounted, ref } from 'vue'
import { useAuthorizationStore } from '#stores/useAuthStore.js'
import { useFeaturesStore } from '#stores/useFeaturesStore.js'
import { useI18n } from 'vue-i18n'
import { useModulesStore } from '#stores/useModulesStore.js'
import { usePermissionsStore } from '#stores/usePermissionsStore.js'
import { usePreferencesStore } from '#stores/usePreferencesStore.js'
import { useRoute, useRouter } from 'vue-router'
import { useSettingsStore } from '#stores/useSettingsStore.js'

const AlertBox = defineAsyncComponent(() => import('#elements/AlertBox.vue'))

const auth = useAuthorizationStore()
const features = useFeaturesStore()
const i18n = useI18n()
const modules = useModulesStore()
const permissions = usePermissionsStore()
const preferences = usePreferencesStore()
const route = useRoute()
const router = useRouter()
const settings = useSettingsStore()

useHead({ title: i18n.t('auth.login') })

const error = ref(false)
const errorMessage = ref(null)
const busy = ref(false)
const username = ref(null)
const password = ref(null)

function handleLogin() {
	Logger.debug('LoginPage.vue:handleLogin: starting login')
	error.value = false
	busy.value = true
	auth
		.login(username.value, password.value)
		.then(async () => {
			if (route.query.r) {
				// console.log('Before navigation:', history.state)
				// Decode the URL-encoded string
				const decodedPath = decodeURIComponent(route.query.r)
				// console.log({ decodedPath })
				// Ensure the path starts with a slash
				const redirectPath = decodedPath.startsWith('/') ? decodedPath : `/${decodedPath}`
				// console.log({ redirectPath })
				// Redirect using Vue Router
				Logger.debug('LoginPage.vue:handleLogin: Redirect using Vue Router:', redirectPath)
				await router.replace({ path: redirectPath })
				// console.log('After navigation:', history.state)
			} else {
				Logger.debug('LoginPage.vue:handleLogin: Redirect to app.start')
				await router.push({ name: 'app.start' })
			}
		})
		.catch(throwErrors)
		.finally(() => {
			busy.value = false
		})
}

/**
 * @param {unknown} e - The error object from a Promise
 */
function throwErrors(e) {
	busy.value = false
	auth.remove()

	switch(e?.response?.status) {
		case httpStatus.UNAUTHORIZED:
			error.value = true
			errorMessage.value = i18n.t('auth.loginWrong')
			break
		case httpStatus.TOO_MANY_REQUESTS:
			if (e.value?.response?.data?.error === 'TEMPORARY_BAN') {
				error.value = true
				errorMessage.value = i18n.t('auth.temporaryBan')
			}
			break
		default:
			error.value = true
			errorMessage.value = i18n.t('auth.loginFailed')
			break
	}
}

onMounted(() => {
	features.remove()
	modules.remove()
	permissions.remove()
	preferences.remove()
	settings.remove()
})
</script>

<template>
	<div
		class="well p-5 rounded mbe-0"
		style="width: 100%; max-width: 30rem;"
	>
		<h1>{{ i18n.t('auth.login') }}</h1>

		<AlertBox
			v-if="error"
			context="negative"
		>
			{{ errorMessage }}
		</AlertBox>

		<form
			action=""
			class="w-100"
			@submit.prevent="handleLogin"
		>
			<fieldset :disabled="busy">
				<FormFieldText
					v-model="username"
					:label="i18n.t('auth.username')"
					autocomplete="username"
					autofocus
					data-testid="username"
					required
				/>

				<FormFieldPassword
					v-model="password"
					:label="i18n.t('auth.password')"
					allow-show
					autocomplete="current-password"
					data-testid="password"
					required
				>
					<template #action>
						<router-link
							tabindex="-1"
							to="/forgot"
						>
							{{ i18n.t('auth.forgotPassword') }}
						</router-link>
					</template>
				</FormFieldPassword>

				<ActionButton
					:loading="busy"
					context="depressed"
					data-testid="login"
					icon="user"
					type="submit"
				>
					{{ i18n.t('auth.login') }}
				</ActionButton>
			</fieldset>
		</form>
	</div>
</template>
